import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Navbar } from "../components/Navbar";
import { FeaturedBadge } from "../components/Featured";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TicinoMap } from "../components/TicinoMap";
import { ComuniCombobox } from "../components/ComuniCombobox";
import { SETTORI_AZIENDALI, PERMESSI_LAVORO } from "../data/settoriSkills";
import { 
  Search, MapPin, Briefcase, Building2, Users, Wrench, ArrowRight, 
  CheckCircle, Map, LayoutGrid, Filter, X, ChevronRight,
  Zap, Phone, Tag
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ListThumb = ({ imageUrl, alt, testId }) => (
  <div className="w-10 h-10 rounded-md border border-slate-200 bg-white overflow-hidden flex items-center justify-center flex-shrink-0" data-testid={testId}>
    {imageUrl ? (
      <img src={imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`} alt={alt} loading="lazy" className="w-full h-full object-cover" />
    ) : (
      <img src="/logo-transparent.png" alt="LavoroTicino.ch" loading="lazy" className="w-4/5 h-4/5 object-contain" />
    )}
  </div>
);


export default function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState({ jobs: 0, services: 0, companies: 0, candidates: 0 });
  const [jobs, setJobs] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // View mode: 'list' or 'map'
  const [viewMode, setViewMode] = useState("list");
  
  // Filters
  const [filters, setFilters] = useState({
    search: "",
    comune: "",
    settore: "all",
    permesso: "all"
  });
  const [showFilters, setShowFilters] = useState(false);

  // SEO: Add Organization JSON-LD schema
  useEffect(() => {
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "LavoroTicino.ch",
      "url": "https://www.lavoroticino.ch",
      "logo": "https://www.lavoroticino.ch/logo192.png",
      "description": "Il portale del Canton Ticino per trovare lavoro, servizi e professionisti. Annunci di lavoro e servizi in Ticino.",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Ticino",
        "addressCountry": "CH"
      },
      "areaServed": {
        "@type": "AdministrativeArea",
        "name": "Canton Ticino, Switzerland"
      },
      "sameAs": [
        "https://www.facebook.com/lavoroticino",
        "https://www.instagram.com/lavoroticino"
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(organizationSchema);
    script.id = 'organization-schema';
    
    // Remove existing if present
    const existing = document.getElementById('organization-schema');
    if (existing) existing.remove();
    
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('organization-schema');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, jobsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/jobs`),
          axios.get(`${API}/services`)
        ]);
        setStats(statsRes.data);
        setJobs(jobsRes.data || []);
        setServices(servicesRes.data || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtered jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      if (filters.search && !job.title?.toLowerCase().includes(filters.search.toLowerCase()) && 
          !job.description?.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      if (filters.comune && job.location !== filters.comune) {
        return false;
      }
      if (filters.settore && filters.settore !== "all" && job.settore !== filters.settore) {
        return false;
      }
      if (filters.permesso && filters.permesso !== "all" && job.permesso_richiesto && job.permesso_richiesto !== filters.permesso) {
        return false;
      }
      return true;
    });
  }, [jobs, filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, search: searchQuery }));
  };

  const clearFilters = () => {
    setFilters({ search: "", comune: "", settore: "all", permesso: "all" });
    setSearchQuery("");
  };

  const activeFiltersCount = [
    filters.search,
    filters.comune,
    filters.settore !== "all" ? filters.settore : "",
    filters.permesso !== "all" ? filters.permesso : ""
  ].filter(v => v).length;

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">

      {/* Hero Section */}
      <div 
        className="hero-overlay min-h-[45vh] sm:min-h-[55vh] flex flex-col bg-cover bg-center"
        style={{ 
          backgroundImage: "url('https://images.pexels.com/photos/15661347/pexels-photo-15661347.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" 
        }}
      >
        <Navbar transparent />
        
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center animate-fadeIn">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight mb-4">
              Lavoro e Servizi<br />
              <span className="text-[#DA291C]">vicino a te</span>
            </h1>
            
            <p className="text-base sm:text-lg text-white/80 max-w-xl mx-auto mb-6">
              Il portale del Canton Ticino per trovare lavoro o clienti
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto" data-testid="search-form">
              <div className="flex gap-2 p-2 bg-white/10 backdrop-blur-md rounded-lg">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Cerca lavoro, ruolo, servizio..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-11 bg-white border-0 focus-visible:ring-2 focus-visible:ring-[#DA291C]"
                    data-testid="search-input"
                  />
                </div>
                <Button 
                  type="submit"
                  className="h-11 px-6 bg-[#DA291C] hover:bg-[#B91C1C] text-white font-semibold"
                  data-testid="search-button"
                >
                  Cerca
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ============================================== */}
      {/* Quick Links - Category Shortcuts */}
      {/* ============================================== */}
      {/* ============================================== */}
      {/* SEZIONI ANNUNCI E SERVIZI - 2 Colonne su Mobile */}
      {/* ============================================== */}
      <div className="flex flex-col gap-3 sm:hidden px-3 py-4 bg-slate-50">
        {/* Colonna Annunci Lavoro - Mobile */}
        <div className="bg-white rounded-xl border p-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-[#DA291C]" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-900">Annunci di lavoro</h3>
            </div>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1,2].map(i => (
                <div key={i} className="h-12 bg-slate-100 rounded animate-pulse"></div>
              ))}
            </div>
          ) : filteredJobs.length > 0 ? (
            <div className="space-y-2">
              {filteredJobs.slice(0, 3).map(job => (
                <Link key={job.id} to={`/jobs/${job.id}`} className="block">
                  <div className="p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-2">
                    <ListThumb imageUrl={job.image_url} alt={job.title} testId={`mobile-job-thumb-${job.id}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-900 line-clamp-1">{job.title}</p>
                      <FeaturedBadge item={job} className="mt-0.5 !text-[9px] !px-1.5" />
                      <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-2.5 h-2.5" />
                        {job.location || "Ticino"}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 text-center py-4">Nessun annuncio</p>
          )}
          <Link to="/jobs" className="block mt-3">
            <Button size="sm" variant="outline" className="w-full text-xs h-7">
              Vedi tutti <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>

        {/* Colonna Servizi - Mobile */}
        <div className="bg-white rounded-xl border p-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Wrench className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-900">Servizi Disponibili</h3>
            </div>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1,2].map(i => (
                <div key={i} className="h-12 bg-slate-100 rounded animate-pulse"></div>
              ))}
            </div>
          ) : services.length > 0 ? (
            <div className="space-y-2">
              {services.slice(0, 3).map(service => (
                <Link key={service.id} to={`/services/${service.id}`} className="block">
                  <div className="p-2 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-2">
                    <ListThumb imageUrl={service.image_url} alt={service.title} testId={`mobile-service-thumb-${service.id}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-900 line-clamp-1">{service.title}</p>
                      <FeaturedBadge item={service} className="mt-0.5 !text-[9px] !px-1.5" />
                      <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-2.5 h-2.5" />
                        {service.location || "Ticino"}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 text-center py-4">Nessun servizio</p>
          )}
          <Link to="/services" className="block mt-3">
            <Button size="sm" variant="outline" className="w-full text-xs h-7 border-emerald-200 text-emerald-700">
              Vedi tutti <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      </div>

      {/* ============================================== */}
      {/* SEZIONE 1: Annunci di Lavoro - Desktop */}
      {/* ============================================== */}
      <section className="hidden sm:block py-10 sm:py-14 bg-slate-50" data-testid="jobs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header with View Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Annunci di Lavoro
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                {filteredJobs.length} {filteredJobs.length === 1 ? "annuncio" : "annunci"} disponibili
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Filter button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`gap-2 ${showFilters ? "bg-slate-100" : ""}`}
              >
                <Filter className="w-4 h-4" />
                Filtri
                {activeFiltersCount > 0 && (
                  <span className="w-5 h-5 bg-[#DA291C] text-white rounded-full text-xs flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>

              {/* View toggle */}
              <div className="flex bg-white rounded-lg border p-1">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded transition-colors ${viewMode === "list" ? "bg-slate-100" : "hover:bg-slate-50"}`}
                  title="Vista lista"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("map")}
                  className={`p-2 rounded transition-colors ${viewMode === "map" ? "bg-slate-100" : "hover:bg-slate-50"}`}
                  title="Vedi sulla mappa"
                >
                  <Map className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-white p-4 rounded-xl border shadow-sm mb-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Filtra annunci</h3>
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 h-8">
                    <X className="w-4 h-4 mr-1" /> Cancella filtri
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Comune */}
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Comune</label>
                  <ComuniCombobox
                    value={filters.comune}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, comune: v }))}
                  />
                </div>

                {/* Settore */}
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Settore</label>
                  <Select
                    value={filters.settore}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, settore: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tutti i settori" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i settori</SelectItem>
                      {SETTORI_AZIENDALI.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Permesso */}
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Permesso richiesto</label>
                  <Select
                    value={filters.permesso}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, permesso: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tutti i permessi" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i permessi</SelectItem>
                      {PERMESSI_LAVORO.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Search in filters */}
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Ricerca</label>
                  <Input
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    placeholder="Parola chiave..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Content based on view mode */}
          {viewMode === "list" ? (
            /* Job Cards Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                // Loading skeleton
                [...Array(6)].map((_, i) => (
                  <div key={`job-skeleton-${i}`} className="bg-white rounded-xl border p-5 animate-pulse">
                    <div className="h-5 bg-slate-200 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-slate-100 rounded w-1/2 mb-4"></div>
                    <div className="h-16 bg-slate-100 rounded mb-4"></div>
                    <div className="h-8 bg-slate-200 rounded w-1/3"></div>
                  </div>
                ))
              ) : filteredJobs.length > 0 ? (
                filteredJobs.map(job => (
                  <Link 
                    key={job.id} 
                    to={`/jobs/${job.id}`}
                    className="group"
                  >
                    <Card className="h-full overflow-hidden hover:shadow-lg transition-all hover:border-[#004E64]/30 group-hover:-translate-y-1">
                      {job.image_url ? (
                        <img
                          src={job.image_url.startsWith('http') ? job.image_url : `${process.env.REACT_APP_BACKEND_URL}${job.image_url}`}
                          alt={job.title}
                          loading="lazy"
                          className="w-full h-36 object-cover"
                          data-testid={`home-job-image-${job.id}`}
                        />
                      ) : (
                        <div className="w-full h-36 bg-slate-50 border-b border-slate-100 flex items-center justify-center" data-testid={`home-job-logo-${job.id}`}>
                          <img src="/logo-transparent.png" alt="LavoroTicino.ch" loading="lazy" className="h-20 object-contain" />
                        </div>
                      )}
                      <CardContent className="p-5">
                        <FeaturedBadge item={job} className="mb-2" />
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-slate-900 group-hover:text-[#004E64] transition-colors line-clamp-1">
                              {job.title}
                            </h3>
                            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <Building2 className="w-3.5 h-3.5" />
                              {job.company_name || "Azienda"}
                            </p>
                          </div>
                          <div className="w-10 h-10 bg-[#004E64]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Briefcase className="w-5 h-5 text-[#004E64]" />
                          </div>
                        </div>
                        
                        <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                          {job.description}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {job.location || "Ticino"}
                            </span>
                            {job.settore && (
                              <span className="px-2 py-0.5 bg-slate-100 rounded-full">
                                {job.settore}
                              </span>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#004E64] transition-colors" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Nessun annuncio trovato con questi filtri</p>
                  <Button variant="outline" size="sm" onClick={clearFilters} className="mt-3">
                    Rimuovi filtri
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Map View */
            <div className="animate-fadeIn">
              <TicinoMap 
                jobs={filteredJobs} 
                services={services} 
                onLocationSelect={(loc) => setFilters(prev => ({ ...prev, comune: loc }))}
                height="500px"
              />
            </div>
          )}

          {/* View all button */}
          {filteredJobs.length > 0 && viewMode === "list" && (
            <div className="text-center mt-8">
              <Link to="/jobs">
                <Button variant="outline" className="gap-2">
                  Vedi tutti gli annunci <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ============================================== */}
      {/* SEZIONE 2: Servizi Disponibili - Desktop */}
      {/* ============================================== */}
      <section className="hidden sm:block py-10 sm:py-14 bg-emerald-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Wrench className="w-7 h-7 text-emerald-600" />
                Servizi Disponibili
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                {services.length} {services.length === 1 ? "servizio" : "servizi"} offerti da professionisti
              </p>
            </div>
            <Link to="/services">
              <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                Vedi tutti i servizi <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={`service-skeleton-${i}`} className="bg-white rounded-xl border p-5 animate-pulse">
                  <div className="h-5 bg-slate-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-slate-100 rounded w-1/2 mb-4"></div>
                  <div className="h-16 bg-slate-100 rounded"></div>
                </div>
              ))
            ) : services.length > 0 ? (
              services.slice(0, 3).map(service => (
                <Link 
                  key={service.id} 
                  to={`/services/${service.id}`}
                  className="group"
                >
                  <Card className="h-full overflow-hidden hover:shadow-lg transition-all hover:border-emerald-300 group-hover:-translate-y-1 bg-white">
                    {service.image_url ? (
                      <img
                        src={service.image_url.startsWith('http') ? service.image_url : `${process.env.REACT_APP_BACKEND_URL}${service.image_url}`}
                        alt={service.title}
                        loading="lazy"
                        className="w-full h-36 object-cover"
                        data-testid={`home-service-image-${service.id}`}
                      />
                    ) : (
                      <div className="w-full h-36 bg-slate-50 border-b border-slate-100 flex items-center justify-center" data-testid={`home-service-logo-${service.id}`}>
                        <img src="/logo-transparent.png" alt="LavoroTicino.ch" loading="lazy" className="h-20 object-contain" />
                      </div>
                    )}
                    <CardContent className="p-5">
                      <FeaturedBadge item={service} className="mb-2" />
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-slate-900 group-hover:text-emerald-600 transition-colors line-clamp-1">
                            {service.title}
                          </h3>
                          <p className="text-sm text-slate-500 mt-1">
                            {service.provider_name || "Professionista"}
                          </p>
                        </div>
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-5 h-5 text-emerald-600" />
                        </div>
                      </div>
                      
                      <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                        {service.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <MapPin className="w-3.5 h-3.5" />
                          {service.location || "Ticino"}
                        </div>
                        {service.rate && (
                          <span className="text-sm font-semibold text-emerald-600">
                            {service.rate}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-8 bg-white rounded-xl border">
                <Wrench className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-3">Nessun servizio ancora pubblicato</p>
                <Link to="/register?type=service_provider">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                    Pubblica il primo servizio
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============================================== */}
      {/* SEZIONE 3: Come Funziona (spostata in basso) */}
      {/* ============================================== */}
      <section className="py-12 sm:py-16 bg-white border-b" data-testid="examples-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Link to="/help" className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 text-sm font-medium rounded-full mb-3 hover:bg-slate-200 transition-colors cursor-pointer">
              <Zap className="w-4 h-4" />
              Come funziona
            </Link>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Due opportunità in un unico portale
            </h2>
            <p className="text-slate-500 mt-2">Esempi reali di annunci sulla piattaforma</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card Servizi - Verde */}
            <div className="group">
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-full">
                    <Tag className="w-3 h-3" />
                    OFFERTA PRIVATO
                  </span>
                  <Wrench className="w-8 h-8 text-white/30" />
                </div>
                
                <h3 className="text-lg font-bold mb-1">Esempio Servizio</h3>
                <div className="flex-1">
                  <p className="text-xl font-semibold mb-2">Sgomberi e Traslochi rapidi</p>
                  <p className="text-white/80 text-sm mb-3">
                    Privato con furgone. Disponibile anche per piccoli lavori e consegne urgenti.
                  </p>
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>Lugano e dintorni</span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-white/20">
                  <Link to="/register?type=service_provider">
                    <Button className="w-full bg-white text-emerald-700 hover:bg-white/90 font-semibold group-hover:scale-[1.02] transition-transform">
                      <Phone className="w-4 h-4 mr-2" />
                      Contatta per il servizio
                    </Button>
                  </Link>
                  <p className="text-center text-xs text-white/60 mt-2">
                    Solo <span className="line-through">CHF 48</span> <span className="font-bold text-white">CHF 24/anno</span> (circa 2 CHF/mese)
                  </p>
                </div>
              </div>
            </div>

            {/* Card Lavoro - Blu */}
            <div className="group">
              <div className="bg-gradient-to-br from-[#004E64] to-[#003B4D] rounded-2xl p-6 text-white h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-full">
                    <Tag className="w-3 h-3" />
                    OFFERTA AZIENDA
                  </span>
                  <Briefcase className="w-8 h-8 text-white/30" />
                </div>
                
                <h3 className="text-lg font-bold mb-1">Esempio Lavoro</h3>
                <div className="flex-1">
                  <p className="text-xl font-semibold mb-2">Cercasi Elettricista qualificato</p>
                  <p className="text-white/80 text-sm mb-3">
                    Installazione impianti civili e industriali. Richiesta esperienza minima 3 anni.
                  </p>
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>Bellinzona</span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-white/20">
                  <Link to="/register?type=candidate">
                    <Button className="w-full bg-white text-[#004E64] hover:bg-white/90 font-semibold group-hover:scale-[1.02] transition-transform">
                      Candidati ora
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <p className="text-center text-xs text-white/60 mt-2">
                    Solo <span className="line-through">CHF 24</span> <span className="font-bold text-white">CHF 12/anno</span> (circa 1 CHF/mese)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Zone Section - Inizia subito */}
      <section className="py-12 sm:py-16 bg-slate-50" data-testid="zones-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Inizia subito
            </h2>
            <p className="text-slate-600 mt-2 max-w-xl mx-auto text-sm sm:text-base">
              Scegli come vuoi usare la piattaforma
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Annunci lavoro */}
            <Card className="group hover:shadow-lg transition-shadow border-2 hover:border-[#004E64]/30">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#004E64]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Briefcase className="w-7 h-7 text-[#004E64]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Sei un'Azienda?</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Pubblica annunci gratuitamente e trova i migliori talenti.
                </p>
                <Link to="/register?type=company">
                  <Button className="w-full bg-[#004E64] hover:bg-[#003B4D] text-white">
                    Pubblica annuncio
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Candidati */}
            <Card className="group hover:shadow-lg transition-shadow border-2 hover:border-[#DA291C]/30">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#DA291C]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Users className="w-7 h-7 text-[#DA291C]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Cerchi Lavoro?</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Crea il tuo profilo e candidati alle offerte.
                </p>
                <Link to="/register?type=candidate">
                  <Button className="w-full bg-[#DA291C] hover:bg-[#B91C1C] text-white">
                    Iscriviti gratis
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Servizi */}
            <Card className="group hover:shadow-lg transition-shadow border-2 hover:border-emerald-500/30">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Wrench className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Offri Servizi?</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Pubblica i tuoi servizi e raggiungi nuovi clienti.
                </p>
                <Link to="/register?type=service_provider">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    Inizia ora
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 sm:py-16 bg-slate-50" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Abbonamenti
            </h2>
            <p className="text-slate-600 mt-2 text-sm">
              Candidati e Aziende si iscrivono gratis!
            </p>
          </div>

          <div className="grid grid-cols-1 max-w-md mx-auto">
            {/* Fornitori - Unico piano a pagamento */}
            <div className="bg-white rounded-xl border-2 border-emerald-500/20 p-6 text-center">
              <div className="inline-block px-3 py-1 bg-emerald-500/10 text-emerald-600 text-xs font-bold rounded-full mb-4">
                FORNITORI DI SERVIZI
              </div>
              <div className="mb-5">
                {/* Annual - Primary */}
                <div className="bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-lg mb-3 inline-block">
                  PIANO ANNUALE
                </div>
                {/* Price - Secondary */}
                <div className="text-2xl font-semibold text-slate-500">24 CHF</div>
                {/* Duration Highlight */}
                <div className="bg-emerald-50 rounded-lg py-2 px-3 mt-3 inline-block">
                  <p className="text-emerald-700 font-bold text-sm">Copertura 365 giorni</p>
                </div>
                <p className="text-xs text-amber-600 mt-2 font-medium">🎁 Codice AGOSTO-GRATIS = GRATIS!</p>
              </div>
              <ul className="space-y-2 text-left text-sm mb-6">
                {["Annunci servizi illimitati", "Contatti diretti clienti", "Portfolio e foto online"].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/register?type=service_provider">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Iscriviti
                </Button>
              </Link>
            </div>
          </div>

          {/* Info candidati e aziende gratis */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              <strong className="text-emerald-600">Candidati</strong> e <strong className="text-[#004E64]">Aziende</strong> si iscrivono <span className="font-bold text-emerald-600">GRATIS</span>!
            </p>
          </div>
        </div>
      </section>

      {/* ============================================== */}
      {/* SEZIONE CONTATORI - In fondo alla pagina */}
      {/* ============================================== */}
      <div className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center gap-8 sm:gap-16">
            {/* Offerte Lavoro */}
            <Link 
              to="/jobs" 
              className="flex flex-col items-center p-3 rounded-xl hover:bg-slate-50 transition-colors group cursor-pointer"
              data-testid="quick-link-jobs"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-2 group-hover:bg-red-100 transition-colors">
                <Briefcase className="w-7 h-7 sm:w-8 sm:h-8 text-[#DA291C]" />
              </div>
              <span className="text-xl sm:text-2xl font-bold text-slate-900">{stats.jobs}</span>
              <span className="text-xs text-slate-500 text-center whitespace-nowrap">Offerte lavoro</span>
            </Link>

            {/* Servizi */}
            <Link 
              to="/services" 
              className="flex flex-col items-center p-3 rounded-xl hover:bg-slate-50 transition-colors group cursor-pointer"
              data-testid="quick-link-services"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-2 group-hover:bg-emerald-100 transition-colors">
                <Wrench className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-600" />
              </div>
              <span className="text-xl sm:text-2xl font-bold text-slate-900">{stats.services}</span>
              <span className="text-xs text-slate-500 text-center whitespace-nowrap">Servizi</span>
            </Link>

            {/* Candidati */}
            <Link 
              to="/candidates" 
              className="flex flex-col items-center p-3 rounded-xl hover:bg-slate-50 transition-colors group cursor-pointer"
              data-testid="quick-link-candidates"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-50 rounded-2xl flex items-center justify-center mb-2 group-hover:bg-purple-100 transition-colors">
                <Users className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600" />
              </div>
              <span className="text-xl sm:text-2xl font-bold text-slate-900">{stats.candidates}</span>
              <span className="text-xs text-slate-500 text-center whitespace-nowrap">Candidati</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
